import React from 'react';

// ── Loading Spinner ─────────────────────────────────────

export function Spinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = { sm: 'w-4 h-4 border-2', md: 'w-8 h-8 border-3', lg: 'w-12 h-12 border-4' };
  return (
    <div
      className={`${sizeClasses[size]} border-md-outline-variant border-t-md-primary rounded-full animate-spin`}
      role="status"
      aria-label="Loading"
    />
  );
}

// ── Loading State ───────────────────────────────────────

export function LoadingState({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <Spinner size="lg" />
      <p className="text-md-on-surface-variant text-sm animate-pulse">{message}</p>
    </div>
  );
}

// ── Empty State ─────────────────────────────────────────

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <div className="text-md-on-surface-variant text-4xl">{icon}</div>
      <h3 className="text-lg font-semibold text-md-on-surface">{title}</h3>
      <p className="text-md-on-surface-variant text-sm max-w-sm">{description}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

// ── Stat Card ───────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string;
  change?: number;
  icon?: React.ReactNode;
  className?: string;
}

export function StatCard({ label, value, change, icon, className = '' }: StatCardProps) {
  return (
    <div className={`md-card-elevated p-5 animate-fade-in ${className}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="md-stat-label">{label}</p>
          <p className="md-stat-value mt-1">{value}</p>
          {change !== undefined && (
            <p className={`text-sm font-medium mt-1 ${change >= 0 ? 'text-md-success' : 'text-md-error'}`}>
              {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(2)}%
            </p>
          )}
        </div>
        {icon && <div className="text-md-on-surface-variant">{icon}</div>}
      </div>
    </div>
  );
}

// ── Modal ───────────────────────────────────────────────

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export function Modal({ isOpen, onClose, title, children, size = 'md' }: ModalProps) {
  if (!isOpen) return null;
  const sizeClasses = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/32" onClick={onClose} />
      <div className={`relative ${sizeClasses[size]} w-full md-card-elevated p-6 animate-slide-up`}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-md-on-surface">{title}</h2>
          <button
            onClick={onClose}
            className="text-md-on-surface-variant hover:text-md-on-surface transition-colors p-1 rounded-md-full hover:bg-md-surface-container-high"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Toggle Switch ───────────────────────────────────────

interface ToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  label: string;
  id: string;
}

export function Toggle({ enabled, onChange, label, id }: ToggleProps) {
  return (
    <label htmlFor={id} className="flex items-center justify-between cursor-pointer group">
      <span className="text-sm text-md-on-surface-variant group-hover:text-md-on-surface transition-colors">{label}</span>
      <button
        id={id}
        role="switch"
        aria-checked={enabled}
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200
          ${enabled ? 'bg-md-primary' : 'bg-md-outline'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full transition-transform duration-200
            ${enabled ? 'translate-x-6 bg-md-on-primary' : 'translate-x-1 bg-md-surface'}`}
        />
      </button>
    </label>
  );
}

// ── Period Selector Tabs ────────────────────────────────

interface PeriodSelectorProps {
  periods: string[];
  selected: string;
  onChange: (period: string) => void;
}

export function PeriodSelector({ periods, selected, onChange }: PeriodSelectorProps) {
  return (
    <div className="flex gap-1 p-1 bg-md-surface-container-high rounded-md-xl border border-md-outline-variant">
      {periods.map((period) => (
        <button
          key={period}
          onClick={() => onChange(period)}
          className={`px-4 py-1.5 text-sm font-medium rounded-md-lg transition-all duration-200
            ${selected === period
              ? 'bg-md-secondary-container text-md-on-secondary-container'
              : 'text-md-on-surface-variant hover:text-md-on-surface hover:bg-md-surface-container-highest'
            }`}
        >
          {period}
        </button>
      ))}
    </div>
  );
}

// ── Export Button ────────────────────────────────────────

interface ExportButtonProps {
  onExport: (format: 'csv' | 'pdf') => void;
  isLoading: boolean;
  label?: string;
}

export function ExportButton({ onExport, isLoading, label = 'Export' }: ExportButtonProps) {
  const [showMenu, setShowMenu] = React.useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={isLoading}
        className="md-btn-tonal text-sm"
        id="export-button"
      >
        {isLoading ? (
          <Spinner size="sm" />
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        )}
        {label}
      </button>
      {showMenu && (
        <div className="absolute right-0 mt-2 w-40 md-card-elevated py-1 z-10 animate-fade-in">
          <button
            onClick={() => { onExport('csv'); setShowMenu(false); }}
            className="w-full text-left px-4 py-2 text-sm text-md-on-surface-variant hover:bg-md-surface-container-high hover:text-md-on-surface transition-colors"
            id="export-csv-button"
          >
            📄 Export CSV
          </button>
          <button
            onClick={() => { onExport('pdf'); setShowMenu(false); }}
            className="w-full text-left px-4 py-2 text-sm text-md-on-surface-variant hover:bg-md-surface-container-high hover:text-md-on-surface transition-colors"
            id="export-pdf-button"
          >
            📑 Export PDF
          </button>
        </div>
      )}
    </div>
  );
}
